import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
  ModalSheetSection,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './ManageSupportingItemsModalForm.module.css'
import { SwitchCheckbox } from '@/components/SwitchCheckbox'
import { Checkbox } from '@/components/Checkbox'

export interface ManageSupportingItemsModalFormProps {
  showing: boolean
  onClose: () => unknown
  onSubmit: () => unknown
  totalSelected: number
  /**
   * A `SupportingItemSelector` per input.
   */
  inputItems?: ReactNode
  /**
   * A `SupportingItemSelector` per document type.
   */
  documentTypeItems?: ReactNode
  testId?: string
}

export function ManageSupportingItemsModalForm({
  showing,
  onClose,
  onSubmit,
  totalSelected,
  inputItems,
  documentTypeItems,
  testId,
}: ManageSupportingItemsModalFormProps) {
  return (
    <ModalSheet
      disableCloseOnOutsideClick
      showing={showing}
      onRequestClose={onClose}
    >
      <ModalSheetHeader withCloseButton bordered>
        <ModalSheetTitle emphasized>Manage supporting items</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ModalSheetSection spaced>
          <div className={styles.heading}>
            <div>Select all that apply</div>
            <div>Mark as required</div>
          </div>
          <div>
            <div className={styles.headingSection}>Inputs</div>
            {inputItems}
          </div>
          <div>
            <div className={styles.headingSection}>Document types</div>
            {documentTypeItems}
          </div>
        </ModalSheetSection>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            testId={testId ? `${testId}-close-button` : undefined}
            onPress={onClose}
            variant="default"
          />

          <Button
            label={
              totalSelected === 1
                ? `Assign ${totalSelected} item`
                : `Assign ${totalSelected} items`
            }
            onPress={onSubmit}
            variant="primary"
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface SupportingItemSelectorProps {
  name: string
  requested: boolean
  onToggleRequested: () => unknown
  required: boolean
  onToggleRequired: () => unknown
}

export function SupportingItemSelector({
  name,
  requested,
  onToggleRequested,
  required,
  onToggleRequired,
}: SupportingItemSelectorProps) {
  return (
    <div className={styles.supportingItem}>
      <div>
        <label className={styles.label}>
          <Checkbox checked={requested} onChange={onToggleRequested} />
          <div className={styles.labelText}>{name}</div>
        </label>
      </div>
      <div>
        <SwitchCheckbox
          noText
          isDisabled={!requested}
          value={required}
          onChange={onToggleRequired}
        />
      </div>
    </div>
  )
}
